import LogoutIcon from '@mui/icons-material/Logout';
import React, { FC } from 'react';
import { useHttpMutation } from '../../../api';
import { API_URLS } from '../consts';
import { useAlert, usePopUpBox } from '../../../shared/components';
import {
  IconButton,
  IconButtonProps,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import { BhabiStorage } from '../../../shared/lib';
import { getUser, setInPlayState } from '../../../redux/reducers';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { dispatch, Store } from '../../../redux';
import { noop } from 'lodash';
import { IN_PLAY_ROUTE } from '../in-play';
import { HOME_ROUTE } from '../home';
import { LoggerService } from '@/shared/logger';
import { SendMessage } from '../use-websocket';

export const LeaveRoom: FC<{
  iconButtonProps?: IconButtonProps;
  avoidIOS?: boolean;
  sendMessage?: SendMessage;
}> = ({ iconButtonProps = {}, sendMessage }) => {
  const onClickIconButton = useLeaveRoom(sendMessage);

  return (
    <IconButton
      onClick={onClickIconButton()}
      color="secondary"
      {...{
        ...iconButtonProps,
        edge: 'end',
        sx: {
          ...iconButtonProps.sx,
          padding: theme => theme.spacing(1),
          marginLeft: 1.5,
        },
      }}
    >
      <LogoutIcon
        sx={{ color: theme => theme.palette.text.primary, fontSize: 26 }}
      />
    </IconButton>
  );
};

const useRematch = () => {
  return useHttpMutation(API_URLS.RE_MATCH, 'GET');
};

export const useLeaveRoom = (sendMessage = noop) => {
  const { mutate: leaveRoom } = useHttpMutation<{ token: string }>(
    API_URLS.LEAVE_ROOM,
    'GET'
  );
  const nav = useNavigate();
  const {
    gameInfo: { room },
  } = useSelector((state: Store) => state.inPlayReducer);
  const isGuest = useSelector(
    (state: Store) => state.userReducer.userType === 'guest'
  );
  const defaultTarget = isGuest ? '/' : HOME_ROUTE.ABSOLUTE_PATH;
  const isRoomOwnerAndConcluded =
    (room?.role === 'owner' && room.isConcluded) ||
    (room?.role === 'owner' && room.userDeck.length === 0);

  const { addPopUpBox, onClick: hidePopUp } = usePopUpBox();
  const { addAlert } = useAlert();
  const { mutate: rematch, isLoading } = useRematch();

  const onClickRematch = () => {
    rematch(undefined, {
      onSuccess: async () => {
        sendMessage('rematch');
        hidePopUp();
        window.location.assign(IN_PLAY_ROUTE.ABSOLUTE_PATH);
      },
      onError: () => {
        addAlert({
          message: 'Error creating rematch',
          type: 'error',
        });
      },
    });
  };
  const onClickLeaveRoom =
    (
      message = `Are you sure you want to leave the room?`,
      useUserLostComponentMessage = false,
      navigateTo = defaultTarget
    ) =>
    () => {
      addPopUpBox(
        {
          message: useUserLostComponentMessage ? (
            <UserLostMessage msg={message} />
          ) : (
            <Typography fontSize={20}>{message}</Typography>
          ),
          callback: status => {
            if (status) {
              leaveRoom(undefined, {
                onSuccess: res => {
                  BhabiStorage.setToken(res.token);
                  dispatch(getUser(res.token));
                  dispatch(
                    setInPlayState({
                      token: res.token,
                      gameInfo: {
                        room: null,
                        userDeck: null,
                        inPlayCards: null,
                        deck: null,
                      },
                      players: [],
                    })
                  );
                  sendMessage('left-room');
                  nav(navigateTo);
                },
                onError: () => {
                  LoggerService.error('Error leaving room');
                  addAlert({
                    message: 'Error leaving room',
                    type: 'error',
                  });
                },
              });
            }
          },
        },
        {
          extraButtonProps: {
            showExtraButton: isRoomOwnerAndConcluded,
            props: {
              onClick: onClickRematch,
              disabled: isLoading,
            },
          },
          texts: {
            title: 'Leave Room',
            yes: 'Leave',
            no: 'Cancel',
            ...(isRoomOwnerAndConcluded ? { extraButton: 'Rematch' } : {}),
          },
        }
      );
    };

  return onClickLeaveRoom;
};

const UserLostMessage: FC<{ msg: string }> = ({ msg }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        // ✅ Blends with the popup (no “paper/card” look)
        width: '100%',
        px: { xs: 0, sm: 0 },
        py: 0,

        // ✅ Place it nicely under "Leave Room" heading
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1,

        // ✅ one line only
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',

        // ✅ small spacing from the title above (adjust if needed)
        mt: 0.75,
      }}
    >
      <Box
        aria-hidden
        sx={{
          fontSize: { xs: 20, sm: 22 },
          lineHeight: 1,
          filter: isDark ? 'drop-shadow(0 2px 8px rgba(0,0,0,0.35))' : 'none',
          flex: '0 0 auto',
        }}
      >
        😵
      </Box>

      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          letterSpacing: '0.01em',

          // ✅ inherit popup vibe; no extra container
          color: isDark
            ? 'rgba(230, 245, 255, 0.85)'
            : 'rgba(15, 23, 42, 0.75)',

          // ✅ keep on one line under heading
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',

          // Optional: slightly smaller so it fits under title
          fontSize: { xs: '0.95rem', sm: '1rem' },
        }}
        title={msg} // full text on long-press/hover
      >
        {msg}
      </Typography>
    </Box>
  );
};
